import { NetworkStatus } from '@apollo/client';
import Raven from 'raven-js';

import { isUngated } from '../utils/isUngated';
import { SCOPES } from '../constants/scopes';
import { useGetInstallabilityQuery } from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc.hooks';
import { ReconnectButtonReconnectabilityGate } from '../constants/gates';
import { hasScope } from '../utils/hasScope';
import { useGetOfferingDetails } from './useGetOfferingDetails';

type TUseIsReconnectableArgs = {
  skip?: boolean;
  listingId?: number;
  appId?: number;
};

export const useIsReconnectable = (args?: TUseIsReconnectableArgs) => {
  const skip = args?.skip ?? false;
  const argsListingId = args?.listingId ?? 0;
  const appId = args?.appId ?? 0;

  const isReconnectabilityUngated = isUngated(
    ReconnectButtonReconnectabilityGate
  );

  const { data: offering, loading: offeringLoading } = useGetOfferingDetails({
    appId,
    skip: skip || !!argsListingId || !isReconnectabilityUngated,
  });

  const listingId = (argsListingId || offering?.listingId) ?? 0;

  const {
    data: installabilityData,
    loading: installabilityLoading,
    networkStatus: installabilityNetworkStatus,
  } = useGetInstallabilityQuery({
    variables: { listingId, published: true },
    skip: skip || !listingId || offeringLoading || !isReconnectabilityUngated,
    onError: (err) => {
      Raven.captureException(err, {
        extra: {
          listingId,
        },
      });
    },
  });

  const loading = offeringLoading || installabilityLoading;

  /**
   * Case: skip check of scopes and installability intentionally
   */
  if (skip || !isReconnectabilityUngated)
    return {
      isReconnectable: true,
      loading,
    };

  const portalInstallable =
    !!installabilityData?.installability?.portalInstallable;
  const userInstallable = !!installabilityData?.installability?.userInstallable;
  /**
   * Case: installability data has been received successfully
   */
  if (
    isReconnectabilityUngated &&
    !!installabilityData &&
    installabilityNetworkStatus === NetworkStatus.ready
  ) {
    return {
      isReconnectable: portalInstallable && userInstallable,
      loading,
    };
  }

  /**
   * Fallback: bases on `integrations-management-write` scope access
   */
  const hasScopeAccess = hasScope(SCOPES.INTEGRATIONS_MANAGEMENT_WRITE);

  return {
    isReconnectable: hasScopeAccess,
    loading,
  };
};
